<template>
	<div class="mqr">
		<!-- /////////////////////// -->
		<!-- OVERLAY -->
		<div @click="onClose" class="mqr__overlay"></div>

		<!-- /////////////////////// -->
		<!-- MODAL -->
		<div class="mqr__modal">
			<!-- ~~~~~~~~~~~~~~~~~~ -->
			<!-- Close icon -->
			<div class="mqr__modal-close">
				<IconClose @click="onClose" class="mqr__modal-close-icon" />
			</div>

			<!-- ~~~~~~~~~~~~~~~~~~ -->
			<!-- Title -->
			<div class="mqr__modal-title">QR код</div>

			<!-- ~~~~~~~~~~~~~~~~~~ -->
			<!-- QR code -->
			<div class="mqr__modal-qrcode">
				<img
					v-if="isShowQrCode"
					class="mqr__modal-qrcode-img"
					:src="qrCodePngBase64"
				/>
				<div v-else class="mqr__modal-qrcode-loading">
					<EllipsisLoader color="rgb(235, 255, 82)" />
				</div>
			</div>

			<!-- Отмена -->
			<div @click="onClose" class="mqr__modal-wrap-cancel">Отмена</div>
		</div>
	</div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import { mapActions, mapGetters } from "vuex";
import { IModal } from "@/interfaces/IModal";
import IconClose from "@/components/icons/IconClose.vue";
import { IQrCode } from "@/interfaces/IQrCode";
import EllipsisLoader from "@/components/preloader/EllipsisLoader.vue";

@Options({
	components: { EllipsisLoader, IconClose },

	computed: {
		...mapGetters("instanceModule", {
			qrCode: "qrCodeGetters",
			modalQrCode: "modalQrCodeGetters",
		}),
	},

	methods: {
		...mapActions("instanceModule", [
			"modalQrCodeAction",
			"qrCodeAction",
			"instanceAction",
		]),
	},
})
export default class ModalQrCode extends Vue {
	public timeId = 0;
	public isShowLoader = true;

	// Константы
	public TIMEOUT = 5000;

	public QR_TYPE = {
		QRCODE: "qrcode",
		LOADING: "loading",
		LOGGED: "alreadyLogged",
	};

	// ************************************************************
	// Define mapGetters
	public qrCode: IQrCode;
	public modalQrCode: {
		data: {
			instance: number;
			token: string;
			channel?: string;
		};
	};

	// ************************************************************
	// Define mapActions
	public modalQrCodeAction!: (payload: IModal) => void;
	public qrCodeAction!: (payload: {
		instance: number;
		token: string;
		channel?: string;
	}) => Promise<void>;
	public instanceAction!: (payload: {
		instanceId: number;
		channel?: string;
	}) => Promise<void>;

	/**
	 * Hook mounted
	 */
	public async mounted() {
		document.body.style.overflowY = "hidden";
		await this.requestQrCode();

		// Таймер 10 сек.
		this.timeId = setInterval(async () => {
			await this.requestQrCode();
		}, this.TIMEOUT);
	}

	/**
	 * Запрашивает QR-код и оставляет заглушку видимой, пока QR еще не готов.
	 */
	public async requestQrCode() {
		this.isShowLoader = true;

		await this.qrCodeAction({
			instance: this.modalQrCode.data.instance,
			token: this.modalQrCode.data.token,
			// Бэкенд по каналу выберет QR endpoint нужного сервиса.
			channel: this.modalQrCode.data.channel,
		});

		switch (true) {
			case this.qrCode.type === this.QR_TYPE.QRCODE:
				this.isShowLoader = false;
				break;

			case this.qrCode.type === this.QR_TYPE.LOADING:
				this.isShowLoader = true;
				break;

			case this.qrCode.type === this.QR_TYPE.LOGGED:
				await this.onCloseLogged();
				break;
		}
	}

	/**
	 * Hook unmounted
	 */
	public unmounted() {
		clearInterval(this.timeId);
	}

	/**
	 * Закрываем модалку по событию QR_TYPE.LOGGED
	 */
	public async onCloseLogged() {
		document.body.style.overflowY = "auto";

		const instanceId = Number(this.$route.params.id);
		await this.instanceAction({
			instanceId: instanceId,
			channel: this.modalQrCode.data.channel,
		});

		this.modalQrCodeAction({ isShow: false });
	}

	/**
	 * Закрываем модалку по событию Click
	 */
	public onClose() {
		document.body.style.overflowY = "auto";
		this.modalQrCodeAction({ isShow: false });
	}

	/**
	 * Картинка QR код
	 */
	public get qrCodePngBase64() {
		return this.qrCode?.message;
	}

	/**
	 * Показывает QR только когда backend вернул готовую картинку.
	 */
	public get isShowQrCode() {
		return (
			!this.isShowLoader &&
			this.qrCode?.type === this.QR_TYPE.QRCODE &&
			Boolean(this.qrCodePngBase64)
		);
	}
}
</script>

<style scoped lang="scss">
.mqr {
	&__overlay {
		display: block;
		position: fixed;
		z-index: 1;
		left: 0;
		top: 0;
		width: 100%;
		height: 100%;
		overflow: auto;
		background-color: rgba(0, 0, 0, 0.4);
	}

	&__modal {
		display: block;
		position: fixed;
		z-index: 2;
		left: 50%;
		top: 50%;
		transform: translate(-50%, -50%);

		background-color: rgb(255, 255, 255);
		border: 1px solid rgb(255, 255, 255);
		border-radius: 12px;
		min-width: 292px;
		min-height: 361px;
	}

	&__modal-close {
		display: flex;
		justify-content: flex-end;
		padding: 16px 16px 0;
	}

	&__modal-close-icon {
		cursor: pointer;
	}

	&__modal-title {
		display: flex;
		justify-content: center;
		color: rgb(49, 49, 50);
		font-size: 18px;
		font-weight: 600;
	}

	&__modal-qrcode {
		display: flex;
		justify-content: center;
		padding: 16px;
	}

	&__modal-qrcode-img {
		width: 200px;
		height: 200px;
	}

	&__modal-qrcode-loading {
		display: flex;
		width: 200px;
		height: 200px;
		align-items: center;
		justify-content: center;
	}

	&__modal-wrap-cancel {
		display: flex;
		justify-content: center;
		padding-top: 18px;
		color: rgb(74, 140, 240);
		cursor: pointer;
	}

	@media screen and (max-width: 768px) {
		&__modal {
			width: 90%;
		}
	}
}
</style>
